import { hasRiskAssessment, snackbarMessage, structureFeatures } from '@/components/Analysis/state';
import { Button } from '@/components/shared/Button/Button';
import { LogInOrOut } from '@/components/shared/LogInOrOut/LogInOrOut';
import { Logo } from '@/components/shared/Navbar/styles';
import { REPORT_A_BUG, USAGE_GUIDE } from '@/constants';
import { BugReport, ChromeReaderMode } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Menu, MenuList, Stack } from '@mui/material';
import Link from '@mui/material/Link';
import { useFeatureFlag, useRouter } from '@/hooks';
import { useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Feature } from '@/types';
import { InfoMenuItem, Navbar, iconStyles } from '@/components/shared/Navbar/Navbar';
import { NavbarMenu } from './NavbarMenu';
import { NavbarLinks, structureInfoBoxStyles } from './styles';
import { StructureSelector } from '@/components/shared/StructureSelector/StructureSelector';
import { StructureName } from '@/components/shared/Breadcrumb';

const AnalysisPages = () => {
  const router = useRouter();
  const { inspection } = router.query;
  const structureInfo = useAtomValue(structureFeatures);
  const setSnackbarMessage = useSetAtom(snackbarMessage);
  const structureHasRiskAssessment = useAtomValue(hasRiskAssessment);

  const tabs: Feature[] = useMemo(() => {
    if (!structureInfo?.features?.navigation) return [];
    return structureInfo?.features?.navigation.filter(
      (feature: Feature) => feature.enabled !== false
    );
  }, [structureInfo?.features?.navigation]);

  const handlePageClick = (route: string) => {
    setSnackbarMessage({
      shouldShow: false,
      content: undefined,
    });
    let path = `/analysis/${inspection}${route}`;
    if (!structureHasRiskAssessment && route.includes('risk')) {
      path = `/es-sheet/${structureInfo?.id}`;
    }
    router.push(path);
  };

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
      {structureInfo?.features?.navigation &&
        tabs.map((tab: Feature, index) => {
          const key = `${tab.flag}_${index}`;
          const isActive = router.routes.includes(tab.url?.replace(/^\//, '') ?? '');

          return (
            <Box key={key}>
              {tab.subNavigation ? (
                <NavbarMenu
                  label={tab.label}
                  items={tab.subNavigation}
                  handleItemClick={handlePageClick}
                />
              ) : (
                <Button
                  onClick={() => handlePageClick(tab.url ?? '')}
                  disabled={!!tab.disabled}
                  sx={{
                    borderColor: isActive ? '#fff' : 'transparent',
                    ...NavbarLinks,
                  }}
                >
                  {tab.label}
                </Button>
              )}
            </Box>
          );
        })}
    </Stack>
  );
};

const LeftNav = ({ inspectionId }: { inspectionId: string }) => {
  const isWorkInProgress = useFeatureFlag('work-in-progress');

  return (
    <Stack direction="row" spacing={3} sx={{ display: 'flex', alignItems: 'center' }}>
      <Link href="/" display="contents">
        <Logo src="/assets/fabric_logo_white.svg" alt="Abyss Fabric" />
      </Link>
      <Box sx={structureInfoBoxStyles}>
        {isWorkInProgress ? <StructureSelector /> : <StructureName inspectionId={inspectionId} />}
      </Box>
    </Stack>
  );
};

const menuItems = [
  {
    url: USAGE_GUIDE,
    icon: <ChromeReaderMode sx={iconStyles} />,
    label: 'Usage guide',
    show: true,
  },
  {
    url: REPORT_A_BUG,
    icon: <BugReport sx={iconStyles} />,
    label: 'Report a bug',
    show: true,
  },
];

const RightNav = () => {
  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const open = Boolean(anchorElement);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(undefined);
  };

  return (
    <Stack direction="row" spacing={4} justifyContent="flex-end" alignItems="center">
      <Button onClick={handleClick} sx={{ color: 'white', minWidth: 0 }}>
        <HelpOutlineIcon />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuList sx={{ p: 0, width: 220 }}>
          {menuItems.map((item) => (
            <InfoMenuItem key={item.label} handleClose={handleClose} {...item} />
          ))}
        </MenuList>
      </Menu>
      <LogInOrOut />
    </Stack>
  );
};

export const AnalysisNavbar = () => {
  const router = useRouter();
  const { inspection } = router.query;

  if (!inspection) {
    router.push('/');
    return <></>;
  }

  return (
    <Navbar
      left={<LeftNav inspectionId={inspection as string} />}
      center={<AnalysisPages />}
      right={RightNav()}
    />
  );
};
